import { GraviGrid, Horizontal } from '@gravitate-js/excalibrr'
import type { ColDef, IFilter, GridApi } from 'ag-grid-community'
import dayjs from 'dayjs'
import { useRef, type MutableRefObject, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import type { InvoiceGroup } from '../../../../api/useFreight/types'
import { useUserSettings } from '../../../../hooks/settings'
import { InvoiceGroupExpand } from './InvoiceGroupExpand'
import { ViewModeRadio } from './ViewModeRadio'
import { DownloadButton } from '../../../_SiteWide/Grid/ActionButtons/DownloadButton'
import type { GridConfigState } from '@gravitate-js/excalibrr/dist/components/GraviGrid/hooks/useAGGridEvent'
import { gridViewManager } from '@/utils/grid'
import { invoiceGroupColDefs } from './groupColDefs'

const STORAGE_KEY = 'freight-invoice-report'

function ActionButtons({
  gridAPIRef,
  setSelectedViewStyle,
  selectedViewStyle,
}: {
  gridAPIRef: MutableRefObject<GridApi<InvoiceGroup> | null>
  setSelectedViewStyle: (viewStyle: 'grouped' | 'flat') => void
  selectedViewStyle: 'grouped' | 'flat'
}) {
  return (
    <Horizontal verticalCenter gap={8}>
      <ViewModeRadio selectedViewStyle={selectedViewStyle} setSelectedViewStyle={setSelectedViewStyle} />
      <DownloadButton fileName={`FreightInvoices_${dayjs().format('YYYY-MM-DD')}.xlsx`} gridAPIRef={gridAPIRef} />
    </Horizontal>
  )
}
export function ByGroupGrid({
  isLoading,
  activeStatus,
  costRevenue,
  setSelectedViewStyle,
  selectedViewStyle,
  serverParams,
  setServerFilters,
  gridFilters,
  filteredData,
}: {
  isLoading: boolean
  filteredData: InvoiceGroup[]
  activeStatus: string
  costRevenue: string
  setSelectedViewStyle: (viewStyle: 'grouped' | 'flat') => void
  selectedViewStyle: 'grouped' | 'flat'
  //@ts-ignore
  serverParams: Record<string, unknown>
  //@ts-ignore
  setServerFilters: (filters: Record<string, unknown>) => void
  //@ts-ignore
  gridFilters: Record<string, unknown>
}) {
  const gridAPIRef = useRef<GridApi<InvoiceGroup> | null>(null)
  const { updateSetting } = useUserSettings()
  const location = useLocation()

  // Column definitions for the data grid
  const columnDefs: ColDef<InvoiceGroup>[] = useMemo(() => invoiceGroupColDefs(), [])

  return (
    <GraviGrid<InvoiceGroup, IFilter>
      key={`invoice-grid-${location.pathname}`}
      primaryKey='id'
      loading={isLoading}
      controlBarProps={{
        title: 'Freight Invoices',
        actionButtons: (
          <ActionButtons
            gridAPIRef={gridAPIRef}
            setSelectedViewStyle={setSelectedViewStyle}
            selectedViewStyle={selectedViewStyle}
          />
        ),
        //@ts-ignore
        serverParams,
        //@ts-ignore
        setFilters: setServerFilters,
        //@ts-ignore
        filters: gridFilters,
      }}
      externalRef={gridAPIRef as MutableRefObject<GridApi<InvoiceGroup>>}
      columnDefs={columnDefs}
      rowData={filteredData}
      storageKey={STORAGE_KEY}
      onGridConfigChanged={(config: GridConfigState) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
      agPropOverrides={{
        context: {
          activeStatus,
          costRevenue,
        },
        rowSelection: 'multiple',
        getRowId: (params: { data: InvoiceGroup }) => params.data?.id,
        rowGroupPanelShow: 'always',
        groupDisplayType: 'groupRows',
        enableRangeSelection: true,
        masterDetail: true,
        detailRowAutoHeight: true,
        detailCellRenderer: InvoiceGroupExpand,
        isRowMaster: (data: InvoiceGroup) => {
          return data?.invoices.length > 1
        },
      }}
      {...gridViewManager(STORAGE_KEY, updateSetting)}
    />
  )
}
